<template>
  <div ref="root" class="dropdown tc-identity" :class="{ show: open }">
    <button
      type="button"
      class="tc-identity-chip"
      aria-haspopup="true"
      :aria-expanded="open"
      data-testid="identity-chip"
      @click="open = !open"
    >
      <span class="tc-identity-avatar" aria-hidden="true">{{ initials }}</span>
      <span class="tc-identity-name" data-testid="identity-name">{{ displayName }}</span>
      <span class="tc-identity-chevron" aria-hidden="true">▾</span>
    </button>

    <div class="dropdown-menu dropdown-menu-end tc-identity-menu" :class="{ show: open }">
      <div class="tc-identity-menu-header" data-testid="identity-menu-name">{{ displayName }}</div>

      <div v-if="activeTarget" class="tc-identity-target-current" data-testid="identity-target-current">
        <span class="tc-identity-target-current-label">Working in</span>
        <span class="tc-identity-target-current-name" data-testid="identity-target-name">{{ activeTarget.displayName }}</span>
      </div>

      <template v-if="targets.length > 1">
        <div class="tc-identity-target-list" data-testid="identity-target-list">
          <button
            v-for="t in targets"
            :key="t.id"
            type="button"
            class="dropdown-item tc-identity-target-item"
            :disabled="t.active || activateMutation.isPending.value"
            :data-testid="`identity-target-switch-${t.id}`"
            @click="switchTarget(t)"
          >
            <span class="tc-identity-target-item-name">{{ t.displayName }}</span>
            <span v-if="t.active" class="tc-identity-target-item-check" aria-hidden="true">&#10003;</span>
            <span
              v-else-if="activateMutation.isPending.value && switchingId === t.id"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
        </div>
      </template>

      <div class="dropdown-divider"></div>

      <div class="tc-identity-menu-item tc-identity-menu-item--static">
        <TimezoneSelector />
      </div>

      <div class="dropdown-divider"></div>

      <form class="tc-identity-attach-form" data-testid="identity-attach-form" @submit.prevent="submitAttach">
        <label class="tc-identity-attach-label" for="identity-attach-org">Attach organization</label>
        <div class="d-flex gap-2">
          <input
            id="identity-attach-org"
            v-model.trim="attachOrg"
            type="text"
            class="form-control form-control-sm"
            placeholder="Organization"
            autocomplete="off"
            data-testid="identity-attach-org-input"
          />
          <button
            type="submit"
            class="btn btn-outline-primary btn-sm"
            :disabled="!attachOrg"
            data-testid="identity-attach-org-submit"
          >Attach</button>
        </div>
      </form>

      <div class="dropdown-divider"></div>

      <button
        v-if="superAdmin || tenantAdmin"
        type="button"
        class="dropdown-item"
        data-testid="admin-nav-btn"
        @click="onAdmin"
      >Admin settings</button>

      <button
        type="button"
        class="dropdown-item"
        data-testid="sign-out-btn"
        :disabled="signingOut"
        @click="emit('signOut')"
      >{{ signingOut ? 'Signing out…' : 'Sign out' }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import TimezoneSelector from './TimezoneSelector.vue';
import { listTargets, activateTarget, attachTargetUrl, type SessionTarget } from '../api/targets';
import { ApiClientError } from '../api/client';

const props = defineProps<{
  displayName: string;
  superAdmin: boolean;
  tenantAdmin: boolean;
  signingOut: boolean;
}>();

const emit = defineEmits<{
  signOut: [];
}>();

const router = useRouter();
const queryClient = useQueryClient();
const root = ref<HTMLElement | null>(null);
const open = ref(false);

// ---------------------------------------------------------------------------
// Multi-target session (ADR-0039) — indicator + switch + attach.
// ---------------------------------------------------------------------------
const targetsQuery = useQuery({
  queryKey: ['targets'],
  queryFn: listTargets,
  staleTime: 30_000,
});

const targets = computed(() => targetsQuery.data.value ?? []);
const activeTarget = computed(() => targets.value.find(t => t.active));

const switchingId = ref<string | null>(null);

const activateMutation = useMutation({
  mutationFn: (id: string) => activateTarget(id),
  onSuccess: () => {
    // Activation rotates the auth cookie and changes which VP every
    // timesheet/draft/project query reads from — a full reload is the
    // only way to guarantee every target-dependent query is refreshed.
    window.location.reload();
  },
  onError: (err) => {
    switchingId.value = null;
    if (err instanceof ApiClientError && err.code === 'target_not_found') {
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
    }
  },
});

function switchTarget(t: SessionTarget): void {
  if (t.active || activateMutation.isPending.value) return;
  switchingId.value = t.id;
  activateMutation.mutate(t.id);
}

const attachOrg = ref('');

function submitAttach(): void {
  const org = attachOrg.value.trim();
  if (!org) return;
  window.location.assign(attachTargetUrl(org));
}

const initials = computed((): string => {
  const parts = props.displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase();
});

function onAdmin(): void {
  open.value = false;
  void router.push('/admin');
}

// Outside-click close (Bootstrap JS is not bundled in this app).
function onOutsideClick(e: MouseEvent): void {
  if (root.value && !root.value.contains(e.target as Node)) {
    open.value = false;
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('mousedown', onOutsideClick);
  } else {
    document.removeEventListener('mousedown', onOutsideClick);
  }
});

onUnmounted(() => {
  document.removeEventListener('mousedown', onOutsideClick);
});
</script>
